import { FC, CSSProperties, ReactNode } from "react";
import theme from "@styles/theme";
import { css } from "@emotion/react";
import { Input, InputProps as BInputProps } from "baseui/input";

export interface InputProps {
  onChangeText?: (value: string) => void;
  name?: string;
  label?: string;
  labelAdornment?: ReactNode;
  value?: string | number;
  placeholder?: string;
  containerStyle?: CSSProperties;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  autoFocus?: boolean;
  inputType?: BInputProps["type"];
  minInputValue?: number;
}

const InputField: FC<InputProps> = ({
  onChangeText,
  name,
  label,
  labelAdornment,
  value,
  placeholder,
  containerStyle,
  disabled,
  required,
  helperText,
  autoFocus = false,
  inputType = "text",
  minInputValue,
}) => {
  return (
    <div css={styles.wrapper}>
      {label && (
        <label
          css={[
            containerStyle?.marginTop && {
              marginTop: containerStyle?.marginTop,
            },
            styles.label,
          ]}
        >
          {label}
          {required && <span css={{ color: theme.colors.negative400 }}>*</span>}
          {labelAdornment}
        </label>
      )}
      <Input
        autoFocus={autoFocus}
        data-baseweb="input-field"
        name={name ?? ""}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        type={inputType}
        min={inputType === "number" ? minInputValue : undefined}
        onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          onChangeText?.(event.currentTarget.value)
        }
        overrides={{
          Root: {
            style: {
              boxShadow: "none",
              backgroundColor: "transparent",
              borderTopWidth: 0,
              borderRightWidth: 0,
              borderBottomWidth: 0,
              borderLeftWidth: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              paddingRight: 0,
            },
          },
          MaskToggleButton: () => null,

          InputContainer: {
            style: ({ $theme, $disabled }) => ({
              backgroundColor: containerStyle?.backgroundColor ?? $theme.colors.white,
              borderTopWidth: "1px",
              borderRightWidth: "1px",
              borderBottomWidth: "1px",
              borderLeftWidth: "1px",
              borderTopStyle: "solid",
              borderRightStyle: "solid",
              borderBottomStyle: "solid",
              borderLeftStyle: "solid",
              borderTopColor: $theme.colors.borderOpaque,
              borderRightColor: $theme.colors.borderOpaque,
              borderBottomColor: $theme.colors.borderOpaque,
              borderLeftColor: $theme.colors.borderOpaque,
              boxShadow: "none",
              width: containerStyle?.width,
              marginTop: containerStyle?.marginTop,
              marginRight: containerStyle?.marginRight,
              marginBottom: containerStyle?.marginBottom,
              marginLeft: containerStyle?.marginLeft,
              ":hover": !$disabled
                ? {
                    borderTopColor: $theme.colors.borderSelected,
                    borderRightColor: $theme.colors.borderSelected,
                    borderBottomColor: $theme.colors.borderSelected,
                    borderLeftColor: $theme.colors.borderSelected,
                  }
                : {},
              ":focus-within": {
                borderTopColor: $theme.colors.borderSelected,
                borderRightColor: $theme.colors.borderSelected,
                borderBottomColor: $theme.colors.borderSelected,
                borderLeftColor: $theme.colors.borderSelected,
              },
            }),
          },

          Input: {
            style: ({ $theme }) => ({
              backgroundColor: "transparent",
              paddingLeft: "12px",
              color: $theme.colors.contentPrimary,
            }),
          },
        }}
      />
      {helperText && <span css={styles.helper}>{helperText}</span>}
    </div>
  );
};

const styles = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
  }),

  label: css({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginBottom: "2px",
    color: theme.colors.contentPrimary,
    fontSize: "0.875rem",
  }),

  helper: css({
    marginTop: "4px",
    fontSize: "0.75rem",
    color: theme.colors.contentSecondary,
  }),
};

export default InputField;
